import {
  Webhook, Clock, Filter, GitBranch, UserCheck, Bot, Layers, Code,
  Database, MessageSquare, Github, Activity, type LucideIcon,
} from 'lucide-react';
import type { WorkflowNodeKind, WorkflowNodeData } from './types';

// Icon registry: serializable iconKey → Lucide component. Nodes store iconKey +
// iconClass strings so the whole graph round-trips through the Worker without JSX.
export const NODE_ICONS: Record<string, LucideIcon> = {
  webhook: Webhook, clock: Clock, filter: Filter, gitBranch: GitBranch,
  userCheck: UserCheck, bot: Bot, layers: Layers, code: Code,
  database: Database, messageSquare: MessageSquare, github: Github,
};

export function iconForKey(iconKey: string): LucideIcon {
  return NODE_ICONS[iconKey] ?? Activity;
}

export type NodeGroup = 'Triggers' | 'Logic' | 'AI' | 'Actions';

export interface PaletteEntry {
  kind: WorkflowNodeKind;
  group: NodeGroup;
  label: string;
  description: string;
  iconKey: string;
  /** Tailwind classes for the palette swatch + card border/bg (light + dark). */
  swatch: string;
  iconClass: string;
  /** Phase B/C executor hint. Forwarded onto the node's data.executor. */
  executor?: string;
  /** Default params pre-filled when the node is dropped. */
  params: Record<string, string | number | boolean>;
}

export const PALETTE: PaletteEntry[] = [
  { kind: 'trigger', group: 'Triggers', label: 'Webhook Trigger', iconKey: 'webhook',
    description: 'Listens for an event (e.g. pull_request opened/synchronise).',
    swatch: 'border-orange-200 bg-orange-50/40 text-orange-800', iconClass: 'w-3.5 h-3.5 text-orange-600',
    params: { url: '/api/webhook/github', events: 'pull_request' } },
  { kind: 'schedule', group: 'Triggers', label: 'Schedule (Cron)', iconKey: 'clock',
    description: 'Fires on a cron schedule.',
    swatch: 'border-blue-200 bg-blue-50/40 text-blue-800', iconClass: 'w-3.5 h-3.5 text-blue-600',
    params: { cron: '0 0 * * *', timezone: 'UTC' } },
  { kind: 'filter', group: 'Logic', label: 'PR Filter', iconKey: 'filter',
    description: 'Filters PRs by repo, branch, or labels.',
    swatch: 'border-sky-200 bg-sky-50/40 text-sky-800', iconClass: 'w-3.5 h-3.5 text-sky-600',
    params: { targetBranch: 'main', labels: '' } },
  { kind: 'condition', group: 'Logic', label: 'Condition', iconKey: 'gitBranch',
    description: 'Branches the flow on a predicate.',
    swatch: 'border-indigo-200 bg-indigo-50/40 text-indigo-800', iconClass: 'w-3.5 h-3.5 text-indigo-600',
    params: { expression: 'score < 80' } },
  { kind: 'approval', group: 'Logic', label: 'Human Approval', iconKey: 'userCheck',
    description: 'Pauses for a human reviewer to approve.',
    swatch: 'border-yellow-200 bg-yellow-50/40 text-yellow-800', iconClass: 'w-3.5 h-3.5 text-yellow-600',
    params: { requiredRole: 'Admin' } },
  { kind: 'aiReview', group: 'AI', label: 'AI Review', iconKey: 'bot',
    description: 'Runs the LLM review agent over the diff.',
    swatch: 'border-purple-200 bg-purple-50/40 text-purple-800', iconClass: 'w-3.5 h-3.5 text-purple-600',
    executor: 'claude-review', params: { model: 'MiniMax-M3', temperature: 0.1 } },
  { kind: 'prompt', group: 'AI', label: 'Prompt / Rules', iconKey: 'layers',
    description: 'System instructions and review rules.',
    swatch: 'border-blue-200 bg-blue-50/40 text-blue-800', iconClass: 'w-3.5 h-3.5 text-blue-600',
    params: { role: 'Senior Security Architect', strictMode: true } },
  { kind: 'tool', group: 'Actions', label: 'Tool (gh/git)', iconKey: 'code',
    description: 'Runs a shell tool: gh, git, or subprocess.',
    swatch: 'border-emerald-200 bg-emerald-50/40 text-emerald-800', iconClass: 'w-3.5 h-3.5 text-emerald-600',
    executor: 'shell', params: { command: 'npm run build', timeoutMs: 8000 } },
  { kind: 'sbom', group: 'Actions', label: 'SBOM Scan', iconKey: 'database',
    description: 'Scans dependencies and CVEs.',
    swatch: 'border-teal-200 bg-teal-50/40 text-teal-800', iconClass: 'w-3.5 h-3.5 text-teal-600',
    executor: 'sbom', params: { minSeverity: 'high' } },
  { kind: 'notify', group: 'Actions', label: 'Notification', iconKey: 'messageSquare',
    description: 'Sends a Slack / Telegram / email notification.',
    swatch: 'border-orange-200 bg-orange-50/40 text-orange-800', iconClass: 'w-3.5 h-3.5 text-orange-500',
    executor: 'notify', params: { channel: 'slack', mentionOnFail: '@security-team' } },
  { kind: 'output', group: 'Actions', label: 'Output / Comment', iconKey: 'github',
    description: 'Posts the review comment back to the PR.',
    swatch: 'border-slate-200 bg-slate-50 text-slate-800', iconClass: 'w-3.5 h-3.5 text-slate-800',
    executor: 'gh-comment', params: { addLabels: 'ai-reviewed', autoApprove: false } },
];

const BY_KIND: Record<WorkflowNodeKind, PaletteEntry> =
  PALETTE.reduce((acc, e) => { acc[e.kind] = e; return acc; }, {} as Record<WorkflowNodeKind, PaletteEntry>);

/** Build a fresh RF node data object for a dropped palette kind. */
export function nodeDataForKind(kind: WorkflowNodeKind): WorkflowNodeData {
  const e = BY_KIND[kind];
  return {
    kind,
    label: e.label,
    description: e.description,
    iconKey: e.iconKey,
    iconClass: e.iconClass,
    executor: e.executor,
    params: { ...e.params },
  };
}

export const NODE_GROUPS: NodeGroup[] = ['Triggers', 'Logic', 'AI', 'Actions'];
